import { z } from 'zod';
import { emptyToNull } from './empty-to-null';

/**
 * Helper function to parse an unknown value to a number or null.
 * - Leaves numbers as numbers.
 * - Converts formatted numeric strings (e.g. "1,234.56") to numbers.
 * - Converts empty strings or invalid types to null.
 */
const parseNumberOrNull = (value: unknown): number | null => {
  const normalizedValue = emptyToNull(value);

  if (normalizedValue === null) {
    return null;
  }

  // Only strings and numbers can be parsed into a valid number
  const isSupportedType =
    typeof normalizedValue === 'string' || typeof normalizedValue === 'number';
  if (!isSupportedType) {
    return null;
  }

  // Remove commas used as thousand separators
  const numericStringWithoutCommas = String(normalizedValue).replaceAll(',', '');
  const parsedNumber = Number.parseFloat(numericStringWithoutCommas);

  // If the parsed result is NaN (e.g., from 'abc'), fallback to null
  return Number.isNaN(parsedNumber) ? null : parsedNumber;
};

/**
 * Schema to convert numeric Strings into a Number or null.
 */
export const numericSchema = z.preprocess(parseNumberOrNull, z.number().nullable());
